import { Box, Button, Typography } from '@material-ui/core';
import { FC, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useMounted } from 'src/hooks/use-mounted';
import {
  BankDetails,
  BasicProfile,
  BrandsProducts,
  ChannelPartner,
  DocumentDetails,
  DayAreas,
  ServiceType,
} from 'src/interfaces/new-channel-partner/channelPartner';
import { BreadCrumbView } from 'src/components/breadcrumbs';
import { ResponseCodes } from 'src/enums/responseCodes';
import { ResourceLoading } from 'src/components/resource-loading';
import { NotFound } from 'src/containers/not-found';
import { channelPartnerApi } from 'src/api/newChannelPartner/channelPartner';
import { channelPartnerApi as channelPartnerApiCognito } from 'src/api/channelPartner';
import toast from 'react-hot-toast';
import { CircularLoader } from 'src/components/common/circularLoader';
import { s3FileApi } from 'src/api/s3file';
import {
  EWASTE_SERVICE_ID,
  PLASTIC_WASTE_SERVICE_ID,
  UCO_WASTE_SERVICE_ID,
} from 'src/constants/common-constant';
import { REPAIR_SERVICE_ID } from 'src/constants/repairRequest-constants';
import { ActivityHistoryLog } from 'src/components/common/activity-history-table';
import { ResourceError } from '../../resource-error';
import { BasicInfoCard } from './cards/channelPartnerDetails/basicInfoCard';
import { BankInfoCard } from '../../common/bankDetails/bankInfoCard';
import { ServiceInfoCard } from './cards/channelPartnerDetails/serviceInfoCard';
import { ShowBrandsProductsCard } from '../../common/ProductBrandSection/showBrandsProducts';
import { ServiceAreaCard } from './cards/channelPartnerDetails/serviceAreaCard';
import { KycDocumentsCard } from './cards/channelPartnerDetails/kycDocumentsCard';
import { StatusDetailsCard } from './cards/StatusDetailsCard';
import { ApprovalCard } from './cards/approvalCard';
import { SubmitProfileCard } from './cards/channelPartnerDetails/submitProfileCard';
import { ShowEWastePincodeDayCard } from './cards/channelPartnerDetails/eWastePincodeDayCard';

interface ChannelPartnerDetailsState {
  data?: ChannelPartner;
  error?: string;
  isLoading: boolean;
  statusCode?: number;
}

export const ChannelPartnerApprove: FC = () => {
  const { id } = useParams();
  const mounted = useMounted();
  const [channelPartnerDetailsState,
    setChannelPartnerDetailsState] = useState<ChannelPartnerDetailsState>({ isLoading: true });
  const [loading, setLoading] = useState<boolean>(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [showSendLink, setShowSendLink] = useState(false);

  const breadCrumbsDetails = [
    {
      url: '/dashboard/channel-partners/main/invited',
      label: 'Invited',
    },
    {
      url: '',
      label: 'Approve',
    },
  ];
  const updateCpState = (data) => {
    setChannelPartnerDetailsState(() => ({
      isLoading: false,
      data,
    }));
  };
  const getCpDetails = useCallback(async () => {
    setChannelPartnerDetailsState(() => ({ isLoading: true }));
    try {
      const result = await channelPartnerApi.getChannelPartnerById(id);
      if (mounted.current) {
        setChannelPartnerDetailsState(() => ({
          isLoading: false,
          data: result?.responseData,
        }));
      }
      const congnitoData = await channelPartnerApi.getChannelPartnerFromCognito(
        result.responseData.userName
      );
      if (congnitoData.responseData) {
        const attributes = congnitoData.responseData.UserAttributes;
        const challengeAttr = attributes.filter((attr) => attr.Name === 'custom:authChallenge_req');
        if (challengeAttr.length) {
          setShowSendLink(challengeAttr[0].Value === 'true');
        }
      }
    } catch (err) {
      console.error(err);

      if (mounted.current) {
        setChannelPartnerDetailsState(() => ({
          isLoading: false,
          error: err.message,
        }));
      }
    }
  }, []);

  const updateBasicDetails = async (basicInfo: BasicProfile): Promise<boolean> => {
    setLoading(true);
    const updateResult = await channelPartnerApi.updateBasicDetails(
      basicInfo,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Basic Details Updated');
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const updateBankDetails = async (bankInfo: BankDetails): Promise<boolean> => {
    setLoading(true);
    const isIfscExist = channelPartnerDetailsState?.data?.bankDetails?.ifscCode;
    const updateResult = await channelPartnerApi.updateBankDetails(
      bankInfo,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      if (isIfscExist) {
        toast.success('Bank Details Updated');
      } else {
        toast.success('Bank Details Added');
      }
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const addService = async (serviceType: ServiceType): Promise<boolean> => {
    setLoading(true);
    const updateResult = await channelPartnerApi.addService(
      serviceType,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Service Added');
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const updateService = async (serviceType: ServiceType): Promise<boolean> => {
    setLoading(true);
    const updateResult = await channelPartnerApi.updateService(
      serviceType,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Service Updated');
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const deleteService = async (serviceTypeId: string) => {
    setLoading(true);
    const updateResult = await channelPartnerApi.deleteService(
      serviceTypeId,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Service Deleted');
      updateCpState(updateResult?.responseData);
    } else {
      toast.error(updateResult?.responseData);
    }
    setLoading(false);
  };

  const addBrand = async (brandProducts: BrandsProducts): Promise<boolean> => {
    setLoading(true);
    const updateResult = await channelPartnerApi.addBrand(
      brandProducts,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Brand and Products Added');
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const updateBrand = async (brandProducts: BrandsProducts): Promise<boolean> => {
    setLoading(true);
    const updateResult = await channelPartnerApi.updateBrand(
      brandProducts,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Products Updated');
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const deleteBrand = async (brandId: string) => {
    setLoading(true);
    const updateResult = await channelPartnerApi.deleteBrand(
      brandId,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Brand Deleted');
      updateCpState(updateResult?.responseData);
    } else {
      toast.error(updateResult?.responseData);
    }
    setLoading(false);
  };

  const addServiceAreas = async (serviceAreaIds: string[]): Promise<boolean> => {
    setLoading(true);
    const updateResult = await channelPartnerApi.addServiceAreas(
      serviceAreaIds,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Service Areas Added');
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const deleteServiceArea = async (serviceAreaIds: string) => {
    setLoading(true);
    const updateResult = await channelPartnerApi.deleteServiceArea(
      serviceAreaIds,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('ServiceArea Deleted');
      updateCpState(updateResult?.responseData);
    } else {
      toast.error(updateResult?.responseData);
    }
    setLoading(false);
  };

  const addDocument = async (document: DocumentDetails, selectedFile: any): Promise<boolean> => {
    setLoading(true);
    const uploadResult = await s3FileApi.uploadMultiple(
      'channelPartner',
      channelPartnerDetailsState?.data?.id,
      [selectedFile]
    );
    const fileDoc = { ...document };
    fileDoc.file.key = uploadResult.data[0].key;
    const updateResult = await channelPartnerApi.addDocument(
      fileDoc,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Document Uploaded');
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const deleteDocument = async (objectId: string) => {
    setLoading(true);
    const updateResult = await channelPartnerApi.deleteDocument(
      objectId,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('Document Deleted');
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const addProfilePhoto = async (selectedFile: any) => {
    const isProfilePicAvailable = channelPartnerDetailsState.data?.profileImage?.name;
    setLoading(true);
    const uploadResult = await s3FileApi.uploadMultiple(
      'channelPartner',
      channelPartnerDetailsState?.data?.id,
      [selectedFile]
    );
    const fileDoc = {
      name: selectedFile?.name,
      type: selectedFile?.type,
      key: uploadResult.data[0].key,
    };
    const updateResult = await channelPartnerApi.addProfilePhoto(
      fileDoc,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      if (isProfilePicAvailable) {
        toast.success('Profile Photo Updated');
      } else {
        toast.success('Profile Photo Uploaded');
      }
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const addPincodeDay = async (dayAreas: DayAreas): Promise<boolean> => {
    setLoading(true);
    const updateResult = await channelPartnerApi.addPincodeDay(
      dayAreas,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('PincodeDay Added');
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const updatePincodeDay = async (dayAreas: DayAreas): Promise<boolean> => {
    setLoading(true);
    const updateResult = await channelPartnerApi.updatePincodeDay(
      dayAreas,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('PincodeDay Updated');
      updateCpState(updateResult?.responseData);
      setLoading(false);
      return true;
    }
    toast.error(updateResult?.responseData?.message?.toString());
    setLoading(false);
    return false;
  };

  const deletePincodeDay = async (day: number) => {
    setLoading(true);
    const updateResult = await channelPartnerApi.deletePincodeDay(
      day,
      channelPartnerDetailsState?.data?.id
    );
    if (updateResult.statusCode === ResponseCodes.OK) {
      toast.success('PincodeDay Deleted');
      updateCpState(updateResult?.responseData);
    } else {
      toast.error(updateResult?.responseData);
    }
    setLoading(false);
  };

  const getActivityLogs = async () => {
    const { responseData } = await channelPartnerApi.getActivityLogsById(id);
    const sortedActivity = responseData.sort(
      (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    setActivityLogs(sortedActivity);
  };

  const resendInviteHandler = async () => {
    const attributes = {
      name: channelPartnerDetailsState.data.name,
      email: channelPartnerDetailsState.data.primaryEmail,
      id: channelPartnerDetailsState.data.id,
    };
    const resendCPInviteRes = await channelPartnerApiCognito.resendChannelPartnerInvite(attributes);
    if (resendCPInviteRes.statusCode === ResponseCodes.CREATED) {
      toast.success('Link sent successfully!');
    } else {
      toast.error('Something went wrong');
    }
  };

  useEffect(() => {
    getCpDetails().catch(console.error);
  }, []);

  useEffect(() => {
    getActivityLogs().catch(console.error);
  }, [loading]);

  const renderContent = () => {
    if (channelPartnerDetailsState.isLoading) {
      return <ResourceLoading />;
    }

    if (!channelPartnerDetailsState.data) {
      return <NotFound />;
    }

    if (channelPartnerDetailsState.statusCode === ResponseCodes.NOT_FOUND) {
      return <NotFound />;
    }

    if (
      channelPartnerDetailsState.error
      && channelPartnerDetailsState.statusCode !== ResponseCodes.NOT_FOUND
    ) {
      return <ResourceError />;
    }
    return (
      <>
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              px: 2,
            }}
          >
            <Box sx={{ py: 4 }}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  pb: 3,
                }}
              >
                <Typography
                  color="textPrimary"
                  variant="h4"
                >
                  {channelPartnerDetailsState.data?.name}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {showSendLink && (
                  <Button
                    sx={{ mr: 3 }}
                    color="primary"
                    size="medium"
                    variant="contained"
                    onClick={resendInviteHandler}
                  >
                    Resend Link
                  </Button>
                )}
              </Box>
              <BreadCrumbView breadCrumbs={breadCrumbsDetails} />
            </Box>

            <Box
              sx={{
                flexGrow: 1,
              }}
            >
              <StatusDetailsCard
                status={channelPartnerDetailsState.data.status}
                activityLogs={activityLogs}
              />
              <BasicInfoCard
                cpDetailData={channelPartnerDetailsState.data}
                addProfilePhoto={addProfilePhoto}
                updateBasicDetails={updateBasicDetails}
              />
              <KycDocumentsCard
                documentDetails={channelPartnerDetailsState.data.documentDetails}
                addDocument={addDocument}
                deleteDocument={deleteDocument}
              />
              <BankInfoCard
                bankDetails={channelPartnerDetailsState.data?.bankDetails}
                updateBankDetails={updateBankDetails}
              />
              <ServiceAreaCard
                serviceAreaIds={channelPartnerDetailsState.data?.serviceAreaIds}
                addServiceAreas={addServiceAreas}
                deleteServiceArea={deleteServiceArea}
              />
              <ServiceInfoCard
                serviceTypes={channelPartnerDetailsState.data?.services}
                addService={addService}
                updateService={updateService}
                deleteService={deleteService}
              />
              {[EWASTE_SERVICE_ID, UCO_WASTE_SERVICE_ID, PLASTIC_WASTE_SERVICE_ID]
                .includes(channelPartnerDetailsState.data?.services[0]?.id) && (
                <ShowEWastePincodeDayCard
                  pincodeDays={channelPartnerDetailsState.data?.dayAreas}
                  serviceIds={channelPartnerDetailsState.data?.serviceAreaIds}
                  deletePincodeDay={deletePincodeDay}
                  addPincodeDay={addPincodeDay}
                  updatePincodeDay={updatePincodeDay}
                />
              )}
              {channelPartnerDetailsState.data?.services[0]?.id === REPAIR_SERVICE_ID && (
                <ShowBrandsProductsCard
                  brandsProducts={channelPartnerDetailsState.data?.brandsProducts}
                  addBrand={addBrand}
                  updateBrand={updateBrand}
                  deleteBrand={deleteBrand}
                />
              )}
              <ApprovalCard
                channelPartner={channelPartnerDetailsState.data}
                setLoading={setLoading}
              />
              <SubmitProfileCard
                cpData={channelPartnerDetailsState.data}
                submitProfile={null}
              />
              <ActivityHistoryLog id={channelPartnerDetailsState.data?.id} />
            </Box>
          </Box>
        </Box>
        {loading && <CircularLoader />}
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>Channel Partner | ServCare</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          flexGrow: 1,
        }}
      >
        {channelPartnerDetailsState && renderContent()}
      </Box>
    </>
  );
};
